/**
 * The opening block every full section shares: a small uppercase eyebrow, an
 * `<h2>`, and an optional one-paragraph lede, all centred on the page's axis.
 *
 * One component rather than three copies per section, because the redesign
 * gives the page six sections that open identically and the outline depends on
 * every one of them agreeing: the hero holds the page's single `<h1>`, these
 * are the `<h2>`s beneath it, and anything with a heading inside a section
 * (step cards, feature cards) sits at `<h3>`. A section that hand-rolled its
 * own head is a section that can drift a level.
 *
 * The eyebrow is decoration for sighted scanning — the heading beside it
 * carries the section's name on its own — so it stays a plain `Text` rather
 * than a heading, and screen readers meet the `<h2>` first.
 */

import { StyleSheet, View } from "react-native";
import { Text } from "react-native-paper";
import { headingProps } from "@/lib/heading-props";
import { useAppTheme } from "@/theme/paper-theme";
import { spacing } from "@/theme/tokens";
import { PANEL_MEASURE, PROSE_MEASURE } from "@/theme/web-tokens";

const SECTION_HEADING_PROPS = headingProps(2);

/** The lede's prose measure, shared with the stance sections it replaces. */

interface SectionHeadProps {
  eyebrow: string;
  heading: string;
  lede?: string;
}

export function SectionHead({ eyebrow, heading, lede }: SectionHeadProps) {
  const theme = useAppTheme();

  return (
    <View style={styles.head}>
      <Text
        variant="labelMedium"
        style={[styles.eyebrow, { color: theme.colors.tertiary }]}
      >
        {eyebrow}
      </Text>

      <Text
        variant="headlineMedium"
        style={[styles.centered, { color: theme.colors.onSurface }]}
        {...SECTION_HEADING_PROPS}
      >
        {heading}
      </Text>

      {lede !== undefined ? (
        <Text
          variant="bodyLarge"
          style={[
            styles.centered,
            styles.lede,
            { color: theme.colors.onSurfaceVariant },
          ]}
        >
          {lede}
        </Text>
      ) : null}
    </View>
  );
}

const styles = StyleSheet.create({
  head: {
    alignItems: "center",
    gap: spacing.sm,
    alignSelf: "center",
    maxWidth: PANEL_MEASURE,
    width: "100%",
  },
  eyebrow: {
    letterSpacing: 2,
    textTransform: "uppercase",
    textAlign: "center",
  },
  // Paper's `Text` left-aligns every variant; wrapped lines stay centred only
  // if this says so.
  centered: {
    textAlign: "center",
  },
  lede: {
    maxWidth: PROSE_MEASURE,
  },
});
